// The change-log surface shared by the geography history and the nomenclator
// history (wireframe IstoricLocalitati / IstoricNomenclator): the same table,
// the same filters, the same paging, differing only in which history kinds it
// reads and whether the entity column is worth a column.
import { Fragment, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudSelect, MudTable, MudTag } from '@egov-moldova/mud-react';
import {
  dateInputToIso,
  EmptyRegisterMessage,
  FilterBar,
  formatDate,
  formatTime,
  isoToDateInput,
  type ActiveFilter,
  DraftDateInput,
} from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { TableFooter, usePageSize } from '../../components/TableFooter/TableFooter';
import { useRoleLabel } from '../../hooks/use-observer-roles';
import { AUDIT_SOURCES, fetchHistory, type HistoryEntityKind, type HistoryEntry } from './api';
import { formatIdentityValue, HistoryChanges } from './HistoryChanges';
import {
  HISTORY_PERIOD_IDS,
  isPeriodApplied,
  isPeriodOrdered,
  periodRange,
  todayIso,
  type HistoryPeriodId,
} from './history-period';
import { HistoryMeta } from './HistoryMeta';

// One letter over 13 281 rows returns the whole table; the server draws the same
// line, and the geography tree drew it first (GeographyPage MIN_QUERY_LENGTH).
const MIN_QUERY_LENGTH = 2;
const QUERY_DEBOUNCE_MS = 250;

// The sources that mean a machine did it. `USER_UI` is a person, and `SYSTEM`
// is what the trail wrote back when it could not tell the two apart — neither
// belongs here.
const AUTOMATIC_SOURCES: ReadonlySet<string> = new Set([
  'NOMENCLATOR_SYNC',
  'CUATM_IMPORT',
  'RSUD_SYNC',
  'MCONNECT_EVENT',
  'REGISTRATOR_API',
  'STALENESS_SWEEP',
]);

interface CatalogHistoryLogProps {
  kinds: HistoryEntityKind[];
  entityId?: string | undefined;
  showEntityColumn: boolean;
  entityColumnLabel: string;
  // What the search box says it searches, which differs by surface: the whole
  // nomenclator names the medicine, one medicine's own log does not, and the
  // geographic log names the CUATM code (the wireframe's three placeholders).
  searchPlaceholder: string;
}

interface Filters {
  // Which preset the reader chose. `from`/`to` stay the transport — they are what
  // the query sends — and the id is what the panel and the chip read back, so a
  // preset can say "Ultimele 7 zile" instead of the two dates it stands for.
  period: HistoryPeriodId;
  from: string;
  to: string;
  source: string;
  // Free text over the row's identity, its changed values and its actor.
  q: string;
}

const NO_FILTERS: Filters = { period: '', from: '', to: '', source: '', q: '' };

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  width?: string;
}

export function CatalogHistoryLog({
  kinds,
  entityId,
  showEntityColumn,
  entityColumnLabel,
  searchPlaceholder,
}: CatalogHistoryLogProps): JSX.Element {
  const { t } = useTranslate();
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  // What the reader has typed, which is not yet what the query asks for.
  const [queryDraft, setQueryDraft] = useState('');
  // One-based, because that is what `mud-pagination` speaks and what the reader
  // sees. The keyset cursor it replaced could only walk forward, so a page jump
  // had no answer; the register now offers one and it lands
  // (specs/043-history-total/spec.md).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('catalog-history');

  // The committed value is what the query key reads, so the box does not re-query
  // 13 281 rows on every keystroke. Written here rather than through
  // `applyFilters` so the effect depends only on the two values it reads.
  useEffect(() => {
    const trimmed = queryDraft.trim();
    const committed = trimmed.length >= MIN_QUERY_LENGTH ? trimmed : '';
    if (committed === filters.q) return;
    const timer = setTimeout(() => {
      setFilters((current) => ({ ...current, q: committed }));
      setPage(1);
    }, QUERY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [queryDraft, filters.q]);

  // A backwards interval would return nothing at all, so it is not sent: the
  // panel says what is wrong and the log keeps showing what it showed.
  const periodOrdered = isPeriodOrdered({
    id: filters.period,
    from: filters.from,
    to: filters.to,
  });

  const history = useQuery({
    queryKey: ['admin-history', kinds, entityId ?? '', filters, page],
    queryFn: () =>
      fetchHistory({
        kinds,
        ...(entityId ? { entityId } : {}),
        ...(periodOrdered && filters.from ? { from: filters.from } : {}),
        ...(periodOrdered && filters.to ? { to: filters.to } : {}),
        ...(filters.source ? { source: filters.source } : {}),
        ...(filters.q ? { q: filters.q } : {}),
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
  });

  function applyFilters(next: Partial<Filters>): void {
    setFilters({ ...filters, ...next });
    setPage(1);
  }

  const entries = history.data?.entries ?? [];
  const total = history.data?.total ?? 0;
  // The search term gets no chip: it clears from the box's own ×, which is the
  // wireframe's own rule for the shared bar (BaraFiltre.tsx:35).
  const activeFilters: ActiveFilter[] = [
    ...(isPeriodApplied({ id: filters.period, from: filters.from, to: filters.to })
      ? [
          {
            key: 'period',
            label: t('history.filters.period'),
            value:
              filters.period === 'custom'
                ? t('common.filter.range_value', {
                    from: filters.from ? formatDate(filters.from) : '…',
                    to: filters.to ? formatDate(filters.to) : '…',
                  })
                : t(`history.filters.period_${filters.period}`),
            onRemove: () => applyFilters({ period: '', from: '', to: '' }),
          },
        ]
      : []),
    ...(filters.source
      ? [
          {
            key: 'source',
            label: t('history.filters.source'),
            value: t(`history.source.${filters.source}`),
            onRemove: () => applyFilters({ source: '' }),
          },
        ]
      : []),
  ];

  // The wireframe's widths (IstoricLocalitati.tsx): a narrow date, a quarter for
  // the position, the change takes the rest, the actor is fixed. The position and
  // the actor are 14 and 10rem, not 18 and 12: beside the 368px MUD sidebar (M1) the
  // register is 864px at 1280, and at 18/12 the change column — the one that holds
  // the values — was left 224px, so its words broke mid-word (V-049). At 14/10 it
  // gets 320px, as much as it had on main (312px).
  const columns: TableColumn[] = [
    // 10rem, not 8: the diff column beside it is the widest and busiest cell in the
    // row and sat flush against the timestamp. Moving it right by widening THIS column
    // keeps the header over its own content — padding on the cell alone would have
    // inset the diff while leaving `Modificare` where the table put it.
    { key: 'recordedAt', label: t('history.column.when'), width: '10rem' },
    ...(showEntityColumn ? [{ key: 'entity', label: entityColumnLabel, width: '14rem' }] : []),
    { key: 'change', label: t('history.column.change') },
    { key: 'actor', label: t('history.column.actor'), width: '10rem' },
  ];

  // An empty register keeps its table. The wireframe leaves the head, the
  // frame and the footer standing and puts the message in one centred cell
  // spanning every column (IstoricLocalitati.tsx:54); a box in
  // place of the register hides what the register would even hold. Both
  // QueryState branches render this same node, because mud-table draws its
  // `empty` slot on its own once `rows` is [].
  const register = (
    <>
      <div className="simsm-history__table">
        {/* Every cell is slotted: a date over its time, a label over its
            identity line, a diff, and the three attribution states are each
            markup a cell string cannot hold. */}
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={entries.map((entry) => ({ id: entry.id }))}
          rowStyle="zebra"
          aria-label={t('history.filters.aria_label')}
        >
          <EmptyRegisterMessage>{t('history.empty')}</EmptyRegisterMessage>
          {entries.map((entry, index) => (
            <HistoryCells
              key={entry.id}
              entry={entry}
              index={index}
              showEntity={showEntityColumn}
              // The kind tag earns its place only where the log mixes kinds: the
              // nomenclator log interleaves a medicine and its codes. On the
              // geographic log every row is a position, and the tag "Localitate"
              // sat directly above an identity line reading "Tip Comună / sat",
              // which reads as a contradiction rather than a label. The wireframe
              // draws no tag on either screen (IstoricLocalitati.tsx).
              showKind={kinds.length > 1}
            />
          ))}
        </MudTable>
      </div>

      {/* One footer for a paged register: `mud-pagination` beside
          `Rândurile {from}–{to} din {total}`, `Niciun rând` when it holds none
          (docs/ui-shape.md §Parity A-18) — the shared TableFooter. */}
      <TableFooter
        total={total}
        page={page}
        pageSize={pageSize}
        onPage={setPage}
        onPageSize={setPageSize}
      />
    </>
  );

  return (
    <>
      <FilterBar
        search={{
          value: queryDraft,
          onChange: setQueryDraft,
          placeholder: searchPlaceholder,
          ariaLabel: t('history.filters.search_aria_label'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => {
          setQueryDraft('');
          setFilters(NO_FILTERS);
          setPage(1);
        }}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('history.filters.aria_label'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <>
            {/* Presets first, the interval only when it is asked for — the
                wireframe's `FiltruPerioada`. The register offered the interval
                alone, so "what changed last week" cost two date pickers and the
                arithmetic to fill them. */}
            <div className="simsm-filter-bar__field">
              <MudSelect
                size="medium"
                label={t('history.filters.period')}
                value={filters.period}
                onMudChange={(e: CustomEvent<{ value: string }>) => {
                  const id = e.detail.value as HistoryPeriodId;
                  // A preset carries its own dates; choosing one replaces
                  // whatever interval was there, and choosing `custom` empties
                  // them so the two fields open blank.
                  const range = periodRange({ id, from: '', to: '' }, todayIso());
                  applyFilters({ period: id, from: range.from, to: range.to });
                }}
              >
                {HISTORY_PERIOD_IDS.map((id) => (
                  <option key={id} value={id}>
                    {t(`history.filters.period_${id === '' ? 'all' : id}`)}
                  </option>
                ))}
              </MudSelect>
            </div>
            {filters.period === 'custom' ? (
              <>
                <div className="simsm-filter-bar__field-pair">
                  <div className="simsm-filter-bar__field">
                    {/* No `format`: AGE's own default is `DD/MM/YYYY`, which is
                        the order Moldova reads a date in and what the wireframe
                        writes on the field (`ZZ/LL/AAAA`). The field speaks that
                        order and the filter keeps its wire value in ISO;
                        `isoToDateInput` / `dateInputToIso` are the conversion
                        between them. `min`/`max` stay ISO — AGE compares bounds
                        on the wire value, not the displayed one. */}
                    <DraftDateInput
                      placeholder={t('common.date.placeholder')}
                      size="md"
                      label={t('history.filters.from')}
                      value={isoToDateInput(filters.from)}
                      invalid={!periodOrdered}
                      // The two ends bound each other, so the calendar cannot
                      // offer a date that would read backwards.
                      {...(filters.to ? { max: filters.to } : {})}
                      onMudChange={(e: CustomEvent<{ value: string }>) =>
                        applyFilters({ from: dateInputToIso(e.detail.value) })
                      }
                    />
                  </div>
                  <div className="simsm-filter-bar__field">
                    <DraftDateInput
                      placeholder={t('common.date.placeholder')}
                      size="md"
                      label={t('history.filters.to')}
                      value={isoToDateInput(filters.to)}
                      invalid={!periodOrdered}
                      {...(filters.from ? { min: filters.from } : {})}
                      onMudChange={(e: CustomEvent<{ value: string }>) =>
                        applyFilters({ to: dateInputToIso(e.detail.value) })
                      }
                    />
                  </div>
                </div>
                {/* Said once, under the pair, rather than twice inside it: it is
                    one fact about the interval, not a fault of either date. */}
                {!periodOrdered ? (
                  <p className="simsm-filter-bar__field-error" role="status">
                    {t('history.filters.period_invalid')}
                  </p>
                ) : null}
              </>
            ) : null}
            <div className="simsm-filter-bar__field">
              <MudSelect
                size="medium"
                label={t('history.filters.source')}
                value={filters.source}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  applyFilters({ source: e.detail.value })
                }
              >
                <option value="">{t('history.filters.source_all')}</option>
                {AUDIT_SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {t(`history.source.${source}`)}
                  </option>
                ))}
              </MudSelect>
            </div>
          </>
        }
      />

      <QueryState query={history} isEmpty={entries.length === 0} empty={register}>
        {register}
      </QueryState>
    </>
  );
}

// The cells of one row, slotted into the MUD table by column key and row index.
function HistoryCells({
  entry,
  index,
  showEntity,
  showKind,
}: {
  entry: HistoryEntry;
  index: number;
  showEntity: boolean;
  showKind: boolean;
}): JSX.Element {
  const { t } = useTranslate();

  return (
    <>
      <span slot={`cell-recordedAt-${index}`} className="simsm-history__cell">
        <span>{formatDate(entry.recordedAtIso)}</span>
        <HistoryMeta label={t('history.meta.label.time')} value={formatTime(entry.recordedAtIso)} />
      </span>
      {showEntity ? (
        <span slot={`cell-entity-${index}`} className="simsm-history__cell">
          <span className="simsm-history__entity-label">
            {entry.entityLabel || t('history.entity.unnamed')}
          </span>
          <EntityIdentity entry={entry} />
          {showKind ? (
            <MudTag variant="info" label={t(`history.entity_kind.${entry.entityKind}`)} />
          ) : null}
        </span>
      ) : null}
      <span
        slot={`cell-change-${index}`}
        className="simsm-history__cell simsm-history__cell--change"
      >
        <HistoryChanges entry={entry} />
      </span>
      <span slot={`cell-actor-${index}`} className="simsm-history__cell">
        <HistoryActor entry={catalogAttribution(entry)} />
      </span>
    </>
  );
}

// The label alone does not identify the row. A medicine is DCI **and** form,
// dose, volume and division (caiet §2), and 16 positions share
// `Acidum acetylsalicylicum` — on the label alone their UPDATE rows were one
// indistinguishable event. A geographic position is named beside its type and
// CUATM code. Both sets are read from the event's own snapshot, so the line says
// what the entity was at the time, not what it is now (wireframe
// components/MedicineIdentity.tsx, pages/IstoricLocalitati.tsx).
function EntityIdentity({ entry }: { entry: HistoryEntry }): JSX.Element | null {
  const { t } = useTranslate();
  const attributes = entry.entityAttributes ?? [];
  if (attributes.length === 0) {
    return null;
  }
  return (
    <span className="simsm-history__meta simsm-history__entity-identity">
      {attributes.map((attribute) => (
        // The space after each fact is the only place the line may break: the
        // separator is glued to the fact before it (CSS `::after`, a non-breaking
        // space), so a wrapped cell never starts a line with a lone middot.
        <Fragment key={attribute.field}>
          <span className="simsm-history__entity-fact">
            {/* The colon is the wireframe's, not decoration: its MetadataLine emits
                `<dt>{label}:</dt>` for every pair (MetadataLine.tsx:27), which is what
                separates the label from its value now that the label is no longer
                shouted in capitals. */}
            <span className="simsm-history__entity-fact-label">
              {t(`history.field.${attribute.field}`)}:
            </span>{' '}
            <span className="simsm-history__entity-fact-value">
              {formatIdentityValue(attribute.field, attribute.value, t)}
            </span>
          </span>{' '}
        </Fragment>
      ))}
    </span>
  );
}

// Everything the "by whom" cell needs, and nothing else. Narrower than
// `HistoryEntry` on purpose: the credential trail
// (`auth.system_credential_history`) records who acted but has no `source`
// column at all, and widening it to fit would mean a caller inventing a source
// it does not have. There is no name here either: every trail keeps the masked
// IDNP and the role and never a person's name (CF-63, SEC-14), so the cell has
// nothing else to read.
export interface HistoryAttribution {
  actorId: string;
  actorRole: string;
  source?: string | undefined;
}

// The catalogue trail's author (CF-63, SEC-14): the masked IDNP, the role and the source, and
// never a person's name. The attribution is picked field by field, so a response that still
// carried a name (an older server) cannot put it on the screen (BUG-1-009).
export function catalogAttribution(
  entry: Pick<HistoryEntry, 'actorId' | 'actorRole' | 'source'>,
): HistoryAttribution {
  return { actorId: entry.actorId, actorRole: entry.actorRole, source: entry.source };
}

// The "by whom" cell. Every surface that renders attribution calls this one
// function: the history log, HistoryModal, the Registrar detail page's history
// tab, the credential history and register, and the aggregate history. So a row
// cannot be a person on one screen and "Sistem" on another.
//
// The cell reads the masked identifier, else the role, and names the source as
// the row's `Motiv` (CF-63, SEC-14, BUG-1-009: the trails keep the IDNP and the
// role and never a name, which is shown only from the current session). It reads
// those three fields and nothing else, so a response that still carried a name
// cannot put it on the screen.
//
// The role is NOT written beside the masked identifier (owner ruling,
// 2026-09-10). It is a fact about the person, not about the change, and the
// column has room for two lines that the change actually needs: who did it, and
// why. Where the role is the ONLY thing recorded it still names the actor on the
// first line: a role without an id is still a person, and `site_history` holds
// rows in exactly that shape.
//
// The history holds three states and the interface used to render two: it
// printed "Sistem" whenever `actorId` was empty, which claimed a machine had
// done work nobody had recorded an actor for, and threw away the role of the
// `site_history` rows that name their actor's role but not their id. Existing
// rows are deliberately not backfilled (contracts/audit-actor.md), so the third
// state has to be sayable.
export function HistoryActor({ entry }: { entry: HistoryAttribution }): JSX.Element {
  const { t } = useTranslate();
  const roleLabelFor = useRoleLabel();
  const roleLabel = entry.actorRole ? roleLabelFor(entry.actorRole) : '';
  const source = entry.source ?? '';

  if (entry.actorId || roleLabel) {
    return (
      <>
        <span>{entry.actorId || roleLabel}</span>
        {source ? (
          <HistoryMeta
            label={t('history.meta.label.source')}
            value={t(`history.source.${source}`)}
          />
        ) : null}
      </>
    );
  }

  // An automatic write was still done BY something, and that something is the
  // system — not the route the write arrived on. The cell used to print the
  // route as the actor ("Import CUATM") over "Proces automat", which answers
  // "how" in the column that asks "who". The route moves to `Motiv`, beside the
  // one a person's row carries, so both kinds of row read the same way down the
  // column: who, then why.
  if (AUTOMATIC_SOURCES.has(source)) {
    return (
      <>
        <span>{t('history.actor.system')}</span>
        <HistoryMeta label={t('history.meta.label.source')} value={t(`history.source.${source}`)} />
      </>
    );
  }

  // No name and no machine source: the row predates the actor trail, or comes
  // from a trail that records no source at all. Either way it says so rather
  // than naming the system, which would be the same confident wrongness in the
  // other direction.
  return <span>{t('history.actor.unrecorded')}</span>;
}
